import React, {useMemo} from "react";
import {
    Avatar,
    Box,
    Chip,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    IconButton,
    Paper,
    Tooltip,
    Typography
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {orange} from "@mui/material/colors";
import moment from "moment-timezone";
import {useTranslation} from "react-i18next";

import {MONTH_YEAR_FORMAT} from "../../config/DateTimeConstants";
import {
    formatDateAndTimeRange,
    formatMinutesAsHours,
} from "../../helpers/DateTimeRangeHelper";
import {
    calculateNetAmendmentDurationInMinutes,
    calculateShiftDurationIncludingCheckInTimeInMinutes
} from "../../helpers/ShiftHelper";
import {retrieveServiceTypeIcon} from "../../helpers/ServiceTypeUtils";
import {useTimezone} from "../../hooks/useTimezone";

const styles = {
    dialog: {
        content: {
            padding: '1rem'
        },
        titleRow: {
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between"
        },
        titleActions: {
            display: "flex",
            alignItems: "top",
            justifyContent: "space-between"
        },
        closeButton: {
            marginTop: '-0.25rem'
        },
        divider: {
            borderColor: "grey.300"
        },
        gridContainer: {
            border: "0.06rem solid",
            borderColor: "divider",
            borderRadius: 1,
            overflowX: "auto",
            minWidth: "54rem"
        },
        headerRow: {
            borderBottom: "0.06rem solid",
            borderColor: "divider",
            paddingX: '1rem',
        },
        bodyRow: {
            borderBottom: "0.06rem solid",
            borderColor: "divider",
            paddingX: '1rem'
        },
        cell: {
            display: "flex",
            alignItems: "center",
            fontSize: "0.875rem",
            minHeight: "2.5rem"
        },
        headerCell: {
            display: "flex",
            alignItems: "center",
            minHeight: "2.5rem",
            fontWeight: 600,
            whiteSpace: "nowrap"
        },
        todayChip: {
            color: "primary.main",
            backgroundColor: "#e3f2fd"
        }
    },
    serviceTypeIcon: {
        display: "flex"
    },
    serviceIcon: {
        width: "1.25rem",
        height: "1.25rem"
    },
    serviceAvatar: {
        width: "1.625rem",
        height: "1.625rem",
        backgroundColor: "white"
    },
    amendmentPill: {
        backgroundColor: orange[50],
        color: orange[900],
        fontWeight: 500,
        borderRadius: "0.8rem",
        border: `0.063rem solid ${orange[200]}`
    }
};

export default function PerformerShiftSummaryDialog({open, onClose, performer, currentDate}) {
    const {timezone} = useTimezone();
    const {t} = useTranslation();
    const title = performer?.fullName;
    const dateLabel = moment(currentDate).tz(timezone).format(MONTH_YEAR_FORMAT);
    const shiftCountLabel = t("common.shiftCountLabel", {count: performer?.totalShiftCount ?? 0});

    const shiftRows = useMemo(() => {
        const shiftRows = (performer?.shifts || []).map((shift) => {
            if (!shift) {
                return null;
            }

            const shiftStartTime = shift.performingFrom;
            const shiftEndTime = shift.to;
            const services = Array.from(
                new Set((shift.bookableServiceTypes || [])
                    .map((bookableService) => bookableService?.serviceType?.keycode)
                    .filter(Boolean))
            );
            const shiftAmendments = (performer?.amendments || []).filter((amendment) => amendment?.shift?.id === shift.id);
            const shiftDurationIncludingCheckinInMinutes = calculateShiftDurationIncludingCheckInTimeInMinutes(shift);
            const amendmentDurationInMinutes = calculateNetAmendmentDurationInMinutes(shiftAmendments);
            const {date, time} = formatDateAndTimeRange(shiftStartTime, shiftEndTime, timezone);
            const notes = shiftAmendments
                .map((amendment) => amendment?.notes)
                .filter(Boolean)
                .join(", ");

            return {
                id: shift.id,
                date,
                time,
                services,
                duration: formatMinutesAsHours(shiftDurationIncludingCheckinInMinutes + amendmentDurationInMinutes),
                amendmentCount: shiftAmendments.length,
                notes,
                sortAt: shiftStartTime
            };
        }).filter(Boolean);

        const standaloneAmendmentRows = (performer?.amendments || [])
            .filter((amendment) => !amendment?.shift?.id && amendment?.startTime)
            .map((amendment) => {
                const amendmentDurationInMinutes = amendment?.durationInMinutes ?? 0;
                const netAmendmentDurationInMinutes = calculateNetAmendmentDurationInMinutes([amendment]);
                const amendmentEndTime = moment(amendment.startTime)
                    .add(amendmentDurationInMinutes, "minutes")
                    .toISOString();
                const {date, time} = formatDateAndTimeRange(amendment.startTime, amendmentEndTime, timezone);

                return {
                    id: amendment.id,
                    date,
                    time,
                    services: [],
                    duration: formatMinutesAsHours(netAmendmentDurationInMinutes),
                    amendmentCount: 1,
                    notes: amendment?.notes || "",
                    sortAt: amendment.startTime
                };
            });

        return [...shiftRows, ...standaloneAmendmentRows]
            .sort((left, right) => moment(left.sortAt).valueOf() - moment(right.sortAt).valueOf());
    }, [performer, timezone]);

    const renderServiceTypeIcons = (serviceKeycodes) => {
        const uniqueServiceTypeKeycodes = [...new Set(serviceKeycodes || [])];

        if (uniqueServiceTypeKeycodes.length === 0) {
            return null;
        }

        const serviceTypeicons = retrieveServiceTypeIcon(uniqueServiceTypeKeycodes);

        return (
            <Box sx={styles.serviceTypeIcon}>
                {serviceTypeicons.map((icon, index) => (
                    <Tooltip key={`${icon}-${index}`} title={uniqueServiceTypeKeycodes[index]}>
                        <Avatar sx={styles.serviceAvatar}>
                            <Box component="img" src={icon} alt={uniqueServiceTypeKeycodes[index]} sx={styles.serviceIcon}/>
                        </Avatar>
                    </Tooltip>
                ))}
            </Box>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle sx={styles.dialog.titleRow}>
                <Box>
                    <Typography variant="h5" fontWeight={700}>
                        {title}
                    </Typography>

                    <Typography color="text.secondary">
                        {t("shiftAdmin.performerShiftSummaryDialog.monthAndShiftCount", {
                            monthYear: dateLabel,
                            count: performer?.totalShiftCount ?? 0
                        })}
                    </Typography>
                </Box>
                <Box sx={styles.dialog.titleActions}>
                    <Chip label={shiftCountLabel} sx={styles.dialog.todayChip}/>
                    <IconButton aria-label={t("shiftAdmin.performerShiftSummaryDialog.closeLabel")} onClick={onClose} sx={styles.dialog.closeButton}>
                        <CloseIcon/>
                    </IconButton>
                </Box>
            </DialogTitle>
            <Divider sx={styles.dialog.divider}/>
            <DialogContent sx={styles.dialog.content}>
                <Paper elevation={0} sx={styles.dialog.gridContainer}>
                    <Grid container spacing={1} sx={styles.dialog.headerRow}>
                        <Grid size={1.5} sx={styles.dialog.headerCell}>{t("common.shiftTableColumns.date")}</Grid>
                        <Grid size={2} sx={styles.dialog.headerCell}>{t("common.shiftTableColumns.performingTime")}</Grid>
                        <Grid size={1.5} sx={styles.dialog.headerCell}>{t("common.shiftTableColumns.service")}</Grid>
                        <Grid size={1.5} sx={styles.dialog.headerCell}>
                            <Tooltip title={t("common.shiftTableColumns.totalHoursTooltip")}>
                                {t("common.shiftTableColumns.totalHours")}
                            </Tooltip>
                        </Grid>
                        <Grid size={2} sx={styles.dialog.headerCell}>{t("common.shiftTableColumns.amendments")}</Grid>
                        <Grid size={3.5} sx={styles.dialog.headerCell}>{t("common.shiftTableColumns.notes")}</Grid>
                    </Grid>

                    {shiftRows.map((shift) => (
                        <Grid key={shift.id} container spacing={1} sx={styles.dialog.bodyRow}>
                            <Grid size={1.5} sx={styles.dialog.cell}>{shift.date}</Grid>
                            <Grid size={2} sx={styles.dialog.cell}>{shift.time}</Grid>
                            <Grid size={1.5} sx={styles.dialog.cell}>{renderServiceTypeIcons(shift.services)}</Grid>
                            <Grid size={1.5} sx={styles.dialog.cell}>{shift.duration}</Grid>
                            <Grid size={2} sx={styles.dialog.cell}>
                                {
                                    shift.amendmentCount > 0
                                    &&
                                    <Chip
                                        label={t("common.amendmentCountLabel", {count: shift.amendmentCount})}
                                        sx={styles.amendmentPill}/>
                                }
                            </Grid>
                            <Grid size={3.5} sx={styles.dialog.cell}>
                                <Tooltip placement="bottom-start" title={shift.notes}>
                                    <Typography variant="body2" noWrap>{shift.notes}</Typography>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    ))}
                </Paper>
            </DialogContent>
        </Dialog>
    );
}